import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOrdersByUser, getOrders, getServices, getTimeSlots } from "../../Api/Api";
import {
  FaUser,
  FaClipboardList,
  FaCreditCard,
  FaSignOutAlt,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaEnvelope,
  FaPhone,
  FaSave,
  FaClock,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import defaultServiceImg from "../../Assets/og_technician_real.png";

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const [activeSidebar, setActiveSidebar] = useState("orders");
  const [activeTab, setActiveTab] = useState("pending");
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || null;
    } catch {
      return null;
    }
  });

  // Profile Form state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    mobile: user?.mobile || "",
    address: localStorage.getItem("userLocation") || "Bengaluru, KA",
  });

  const [orders, setOrders] = useState([]);
  const [servicesMap, setServicesMap] = useState({});
  const [slotsMap, setSlotsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    setLoading(true);

    Promise.all([
      getOrdersByUser(user.id).catch(() => []),
      getOrders().catch(() => []),
      getServices().catch(() => []),
      getTimeSlots().catch(() => []),
    ])
      .then(([userOrders, allOrders, servicesList, slotsList]) => {
        // Map services list by ID
        const srvMap = {};
        if (Array.isArray(servicesList)) {
          servicesList.forEach((s) => {
            srvMap[s.id] = s;
          });
        }
        setServicesMap(srvMap);

        // Map timeslots by ID
        const slotMap = {};
        if (Array.isArray(slotsList)) {
          slotsList.forEach((slot) => {
            slotMap[slot.id] = slot;
          });
        }
        setSlotsMap(slotMap);

        // Determine user orders (try getOrdersByUser first, fallback to filtering allOrders)
        let combined = [];
        if (Array.isArray(userOrders) && userOrders.length > 0) {
          combined = userOrders;
        } else if (Array.isArray(allOrders)) {
          combined = allOrders.filter(
            (o) =>
              String(o.user_id) === String(user.id) ||
              (user.email && o.email === user.email) ||
              (user.mobile && String(o.mobile) === String(user.mobile))
          );
        }

        setOrders(combined);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load real orders data", err);
        setOrders([]);
        setLoading(false);
      });
  }, [user, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    toast.success("Logged out successfully", { autoClose: 1000 });
    setTimeout(() => {
      window.location.href = "/";
    }, 1000);
  };

  const handleSaveProfile = (e) => {
    e.preventDefault();
    const updatedUser = { ...user, name: profileForm.name, email: profileForm.email, mobile: profileForm.mobile };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    localStorage.setItem("userLocation", profileForm.address);
    setUser(updatedUser);
    toast.success("Profile details updated successfully!", { autoClose: 1500 });
  };

  function formatTimeToIST(timeStr) {
    if (!timeStr) return "09:00 AM – 01:00 PM";
    try {
      const date = new Date(`1970-01-01T${timeStr}Z`);
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "Asia/Kolkata",
      });
    } catch {
      return timeStr;
    }
  }

  // Filter real orders by status
  const pendingOrders = orders.filter(
    (o) => o.status === "pending" || o.status === "processing" || o.status === "in_progress" || !o.status
  );
  const completedOrders = orders.filter(
    (o) => o.status === "confirmed" || o.status === "completed" || o.status === "delivered"
  );

  const displayOrders = activeTab === "pending" ? pendingOrders : completedOrders;

  return (
    <div className="bg-[#F8F9FA] min-h-screen py-10 px-4 sm:px-6 lg:px-8 lg:py-24 ">
      <ToastContainer position="top-right" />

      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-10">
        
        {/* LEFT SIDEBAR MENU */}
        <div className="w-full lg:w-64 shrink-0 space-y-6">
          {/* User Profile Card */}
          <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#0D004C] text-[#FFB800] flex items-center justify-center font-black text-sm shrink-0 shadow">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="overflow-hidden">
              <h3 className="font-bold text-xs text-gray-900 truncate">{user?.name || "User"}</h3>
              <p className="text-[11px] text-gray-400 truncate">{user?.email || user?.mobile}</p>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="bg-white rounded-2xl p-3 border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] space-y-1">
            <button
              onClick={() => setActiveSidebar("profile")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs transition ${
                activeSidebar === "profile"
                  ? "bg-gray-200/80 text-gray-900 shadow-sm"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <FaUser size={15} />
              <span>Profile Details</span>
            </button>

            <button
              onClick={() => setActiveSidebar("orders")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs transition ${
                activeSidebar === "orders"
                  ? "bg-gray-200/80 text-gray-900 shadow-sm"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <FaClipboardList size={15} />
              <span>My Orders ({orders.length})</span>
            </button>

            <button
              onClick={() => setActiveSidebar("payment")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs transition ${
                activeSidebar === "payment"
                  ? "bg-gray-200/80 text-gray-900 shadow-sm"
                  : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              <FaCreditCard size={15} />
              <span>Payment Methods</span>
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs text-red-600 hover:bg-red-50 transition cursor-pointer mt-4"
            >
              <FaSignOutAlt size={15} />
              <span>Log Out</span>
            </button>
          </div>
        </div>

        {/* RIGHT MAIN CONTENT AREA */}
        <div className="flex-1">
          
          {/* TAB 1: USER PROFILE DETAILS */}
          {activeSidebar === "profile" && (
            <div>
              <div className="mb-6">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                  Profile Details
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Manage your account information, contact details, and primary delivery address.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] space-y-6">
                <form onSubmit={handleSaveProfile} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Full Name */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">Full Name</label>
                      <div className="relative">
                        <FaUser className="absolute left-4 top-3.5 text-gray-400 text-xs" />
                        <input
                          type="text"
                          value={profileForm.name}
                          onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-xs font-semibold outline-none focus:ring-2 focus:ring-[#0D004C]"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">Email Address</label>
                      <div className="relative">
                        <FaEnvelope className="absolute left-4 top-3.5 text-gray-400 text-xs" />
                        <input
                          type="email"
                          value={profileForm.email}
                          onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-xs font-semibold outline-none focus:ring-2 focus:ring-[#0D004C]"
                        />
                      </div>
                    </div>

                    {/* Mobile Number */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">Mobile Phone</label>
                      <div className="relative">
                        <FaPhone className="absolute left-4 top-3.5 text-gray-400 text-xs" />
                        <input
                          type="text"
                          value={profileForm.mobile}
                          onChange={(e) => setProfileForm({ ...profileForm, mobile: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-xs font-semibold outline-none focus:ring-2 focus:ring-[#0D004C]"
                        />
                      </div>
                    </div>

                    {/* Saved Location / Primary Address */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">Primary Service Location</label>
                      <div className="relative">
                        <FaMapMarkerAlt className="absolute left-4 top-3.5 text-gray-400 text-xs" />
                        <input
                          type="text"
                          value={profileForm.address}
                          onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-xs font-semibold outline-none focus:ring-2 focus:ring-[#0D004C]"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-100 flex justify-end">
                    <button
                      type="submit"
                      className="bg-[#0D004C] text-white px-6 py-3 rounded-xl text-xs font-bold hover:bg-[#16007A] transition shadow flex items-center gap-2"
                    >
                      <FaSave />
                      <span>Save Profile Details</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* TAB 2: MY ORDERS LIST (REAL API DATA DISPLAY) */}
          {activeSidebar === "orders" && (
            <div>
              <div className="mb-6">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                  My Orders
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  View and manage your service requests.
                </p>
              </div>

              {/* Pending vs Completed Tabs */}
              <div className="border-b border-gray-200 mb-8 flex gap-8 text-sm font-bold">
                <button
                  onClick={() => setActiveTab("pending")}
                  className={`pb-3 transition relative ${
                    activeTab === "pending"
                      ? "text-gray-900 border-b-2 border-black"
                      : "text-gray-400 hover:text-gray-700"
                  }`}
                >
                  Pending ({pendingOrders.length})
                </button>
                <button
                  onClick={() => setActiveTab("completed")}
                  className={`pb-3 transition relative ${
                    activeTab === "completed"
                      ? "text-gray-900 border-b-2 border-black"
                      : "text-gray-400 hover:text-gray-700"
                  }`}
                >
                  Completed ({completedOrders.length})
                </button>
              </div>

              {/* Orders List Container */}
              {loading ? (
                <div className="space-y-6">
                  {[1, 2].map((i) => (
                    <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm animate-pulse space-y-4">
                      <div className="h-4 w-24 bg-gray-200 rounded-full" />
                      <div className="h-6 w-48 bg-gray-200 rounded-md" />
                      <div className="h-4 w-full bg-gray-100 rounded-md" />
                    </div>
                  ))}
                </div>
              ) : displayOrders.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center border border-gray-100 shadow-sm space-y-4">
                  <div className="text-4xl">📦</div>
                  <h3 className="text-base font-bold text-gray-900">No {activeTab} orders found</h3>
                  <p className="text-xs text-gray-500 max-w-sm mx-auto">
                    You don't have any {activeTab} service requests at the moment. Explore our home services to make your first booking!
                  </p>
                  <button
                    onClick={() => navigate("/")}
                    className="bg-[#0D004C] text-white px-6 py-2.5 rounded-full text-xs font-bold hover:bg-[#16007A] transition shadow"
                  >
                    Explore Services →
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {displayOrders.map((order, idx) => {
                    // Map real service IDs to real Service Names
                    const serviceIds = String(order.services || "").split(",").filter(Boolean);
                    const serviceNamesList = serviceIds
                      .map((id) => servicesMap[id]?.name)
                      .filter(Boolean);

                    const serviceTitle =
                      serviceNamesList.length > 0
                        ? serviceNamesList.join(", ")
                        : order.service_name || `Service Request #${order.id}`;

                    // Real service image if available
                    const firstSrvImg = serviceIds.length > 0 ? servicesMap[serviceIds[0]]?.image : null;
                    const imageSrc = firstSrvImg
                      ? firstSrvImg.startsWith("http")
                        ? firstSrvImg
                        : `http://skishore.in/api/public/${firstSrvImg}`
                      : defaultServiceImg;

                    // Real delivery date & timeslot
                    const dateStr = order.delivery_date || order.created_at?.split(" ")[0] || "Today";
                    const slotObj = order.timeslot_id ? slotsMap[order.timeslot_id] : null;
                    const slotTimeStr = slotObj ? formatTimeToIST(slotObj.start_time) : "Standard Booking Slot";

                    // Real address
                    const fullAddress =
                      order.address ||
                      [order.street, order.landmark].filter(Boolean).join(", ") ||
                      localStorage.getItem("userLocation") ||
                      "Bengaluru, Karnataka";

                    const amount = order.total || order.sub_total || "0.00";
                    const isCompleted = activeTab === "completed";

                    return (
                      <div
                        key={order.id || idx}
                        onClick={() => navigate(`/order/${order.id}`)}
                        className="bg-white rounded-2xl p-6 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-md transition cursor-pointer space-y-4"
                      >
                        {/* Top Row: Status Pill & Price */}
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <img
                              src={imageSrc}
                              alt={serviceTitle}
                              className="w-14 h-14 rounded-xl object-cover border border-gray-100 shrink-0 hidden sm:block"
                            />
                            <div>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`text-[10px] font-black uppercase px-3 py-1 rounded-full tracking-wider ${
                                    isCompleted
                                      ? "bg-emerald-100 text-emerald-800"
                                      : order.status === "in_progress"
                                      ? "bg-amber-400 text-black"
                                      : "bg-gray-200 text-gray-800"
                                  }`}
                                >
                                  {isCompleted ? "Confirmed / Completed" : order.status || "Pending"}
                                </span>
                                <span className="text-xs font-bold text-gray-400">Order #{order.id}</span>
                              </div>
                              <h3 className="text-lg font-bold text-gray-900 mt-2 tracking-tight line-clamp-1">
                                {serviceTitle}
                              </h3>
                            </div>
                          </div>

                          <div className="text-left sm:text-right shrink-0">
                            <div className="text-lg font-black text-gray-900">
                              ₹{!isNaN(Number(amount)) ? Number(amount).toFixed(2) : amount}
                            </div>
                            <div className="text-[10px] text-gray-400 font-semibold flex items-center sm:justify-end gap-1 mt-0.5">
                              <FaCheckCircle className="text-emerald-500" size={10} />
                              <span className="capitalize">{order.payment_method === "cod" ? "Cash on Delivery" : "Paid Online"}</span>
                            </div>
                          </div>
                        </div>

                        {/* Divider & Real Date/Time & Address */}
                        <div className="border-t border-gray-100 pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs font-semibold text-gray-700">
                          <div className="flex items-center gap-2.5">
                            <FaCalendarAlt className="text-gray-400 shrink-0" size={14} />
                            <div>
                              <div className="text-gray-900 font-bold">{dateStr}</div>
                              <div className="text-[11px] text-gray-400 font-normal">{slotTimeStr}</div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2.5 max-w-sm">
                            <FaMapMarkerAlt className="text-gray-400 shrink-0" size={14} />
                            <div>
                              <div className="text-gray-900 font-bold">{order.user_name || user.name}</div>
                              <div className="text-[11px] text-gray-400 font-normal truncate">{fullAddress}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: PAYMENT METHODS */}
          {activeSidebar === "payment" && (
            <div>
              <div className="mb-6">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-[#0D004C] tracking-tight">
                  Payment Methods
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Manage your saved payment preferences and gateway options.
                </p>
              </div>

              <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-[0_4px_24px_rgba(0,0,0,0.04)] space-y-4">
                <div className="flex items-center justify-between p-4 rounded-2xl border border-gray-100 bg-gray-50/50">
                  <div className="flex items-center gap-3">
                    <FaCreditCard className="text-[#0D004C]" size={20} />
                    <div>
                      <h4 className="font-bold text-xs text-gray-900">Razorpay / Online UPI Gateway</h4>
                      <p className="text-[11px] text-gray-400">Instant Checkout Enabled</p>
                    </div>
                  </div>
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 font-extrabold px-3 py-1 rounded-full uppercase">
                    Active
                  </span>
                </div>
              </div>
            </div>
          )}

        </div>

      </div>
    </div>
  );
};

export default MyOrdersPage;
